import { DestroyRef, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BoelsUser,
  BOProduct,
  Page,
  PageTemplate,
  PageViewEvent,
  UserEnum,
} from '@boels/models';
import {
  CartFacade,
  CategoriesFacade,
  CmsFacade,
  CultureFacade,
  ProductFacade,
  UserDataFacade,
} from '@boels/shared-state';
import { filter, first, lastValueFrom, take } from 'rxjs';
import {
  EcommerceTrackingService,
  getPricingDetails,
  GtmService,
  WindowRef,
} from '@boels/helpers';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { checkoutSteps } from '@boels/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const PAGE_NAME_URL_INDEX = 3;

@Injectable()
export class BoelsPageViewTrackingService {
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private cmsFacade = inject(CmsFacade);
  private productFacade = inject(ProductFacade);
  private cartFacade = inject(CartFacade);
  private ecommerceTrackingService = inject(EcommerceTrackingService);
  private cultureFacade = inject(CultureFacade);
  private categoriesFacade = inject(CategoriesFacade);
  private winRef = inject(WindowRef);
  private userDataFacade = inject(UserDataFacade);
  private destroyRef = inject(DestroyRef);

  private activeCheckoutIndex: number | undefined;
  private activePageTemplate: string;

  init(): void {
    if (!this.winRef.isBrowser()) return;

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.setPageViewEvent();
        }
      });
  }

  async setPageViewEvent(): Promise<void> {
    const pageViewEvent = await this.generatePageViewEvent();
    this.gtmService.pushTag(pageViewEvent);

    this.setBeginCheckoutEvent();
    this.setPurchaseEvent();
  }

  async generatePageViewEvent(): Promise<PageViewEvent> {
    const culture = await lastValueFrom(
      this.cultureFacade.getActiveCulture().pipe(first()),
    );

    const activePage = await lastValueFrom(
      this.cmsFacade.getActivePage().pipe(first()),
    );

    const userData = await lastValueFrom(
      this.userDataFacade.userData().pipe(first()),
    );

    this.activePageTemplate = activePage?.template || '';
    const pageName = this.getActivePageName(
      this.activePageTemplate,
      activePage,
      userData,
    );

    let tag: PageViewEvent = {
      event: 'virtual_page_view',
      context: {
        locale: {
          country: culture?.split('-')[1],
          language: culture?.split('-')[0],
        },
        page: {
          type: this.activePageTemplate,
          name: pageName,
        },
        user: {
          user_id: userData.userId,
          is_logged_in: userData.type === UserEnum.B2C_AUTHENTICATED,
        },
      },
    };

    tag = await this.addProductDataToContext(tag, this.activePageTemplate);
    tag = await this.addCheckoutDataToContext(tag, this.activePageTemplate);
    return tag;
  }

  getActivePageName(
    pageTemplate: string,
    activePage: Page | undefined,
    userData: BoelsUser,
  ): string {
    let pageName;
    const routerSegments = this.router.url.split('/');

    switch (pageTemplate) {
      case PageTemplate.PRODUCTLIST:
      case PageTemplate.CATEGORY:
      case PageTemplate.PRODUCT:
        pageName = routerSegments[routerSegments.length - PAGE_NAME_URL_INDEX];
        break;
      default:
        pageName =
          this.getCheckoutDetailsPageName(
            activePage?.name,
            pageTemplate,
            userData,
          ) || `${activePage?.name || ''}`;
    }

    return pageName;
  }

  private getCheckoutDetailsPageName(
    pageName: string | undefined,
    pageTemplate: string,
    userData: BoelsUser,
  ): string | undefined {
    if (
      !pageName ||
      pageTemplate !== PageTemplate.CHECKOUT ||
      !pageName.includes('Checkout Customer Details Page')
    ) {
      return undefined;
    }

    return userData.type === UserEnum.B2C_AUTHENTICATED
      ? 'Debtor Details Page'
      : 'Guest Details Page';
  }

  async addProductDataToContext(
    tag: PageViewEvent,
    pageTemplate: string,
  ): Promise<PageViewEvent> {
    if (pageTemplate === PageTemplate.PRODUCT) {
      const selectedProduct = await lastValueFrom(
        this.productFacade.getCurrentProduct().pipe(filter(Boolean), first()),
      );

      if (!selectedProduct) {
        throw new Error('No selected product found');
      }

      const activeCategoryListingPage = await lastValueFrom(
        this.categoriesFacade.getActiveCategoryListingPage().pipe(take(1)),
      );

      const itemCategory =
        activeCategoryListingPage?.itemCategory?.replace(/\b\w/g, (char) =>
          char.toUpperCase(),
        ) ?? this.getItemCategories(selectedProduct, 2);

      const itemCategory2 =
        activeCategoryListingPage?.itemCategory2?.replace(/\b\w/g, (char) =>
          char.toUpperCase(),
        ) ?? this.getItemCategories(selectedProduct, 1);

      if (!itemCategory || !itemCategory2) {
        throw new Error('No item categories found');
      }
      const { price, currency, pricing_unit } = getPricingDetails(
        selectedProduct?.unitPrices || [],
      );

      tag.context.product = {
        item_category: itemCategory,
        item_category2: itemCategory2,
        item_id: selectedProduct?.code || '',
        item_name: selectedProduct?.name || '',
        is_accessory: selectedProduct?.isAccessory || false,
        price,
        currency,
        pricing_unit,
      };

      if (activeCategoryListingPage) {
        this.categoriesFacade.clearActiveCategoryListingPage();
      }
    }

    if (pageTemplate === PageTemplate.PRODUCTLIST) {
      const routerSegments = this.router.url.split('/');
      this.categoriesFacade.setActiveCategoryListingPage({
        itemCategory:
          routerSegments[routerSegments.length - PAGE_NAME_URL_INDEX - 1],
        itemCategory2:
          routerSegments[routerSegments.length - PAGE_NAME_URL_INDEX],
      });
    }

    return tag;
  }

  async addCheckoutDataToContext(
    tag: PageViewEvent,
    pageTemplate: string,
  ): Promise<PageViewEvent> {
    switch (pageTemplate) {
      case PageTemplate.CHECKOUTACCOUNT:
        this.activeCheckoutIndex = 0;
        break;
      case PageTemplate.CHECKOUT:
        this.activeCheckoutIndex = checkoutSteps.find(
          (step) => step.url && this.router.url.includes(step.url),
        )?.step;
        // if the url is empty then it's step 3 since the delivery mode is known
        this.activeCheckoutIndex = this.activeCheckoutIndex || 3;
        break;
      case PageTemplate.ORDERCONFIRMATION:
        this.activeCheckoutIndex = 5;
        break;
      default:
    }

    if (this.activeCheckoutIndex !== undefined) {
      tag.context.checkout = {
        step: this.activeCheckoutIndex,
      };
    }

    return tag;
  }

  getItemCategories(product: BOProduct | undefined, level: number): string {
    const categories = product?.alternates?.[0]?.nativeBreadcrumbs;
    return categories[categories.length - level];
  }

  async setBeginCheckoutEvent() {
    if (
      this.activeCheckoutIndex !== 1 ||
      this.activePageTemplate !== PageTemplate.CHECKOUT
    ) {
      return;
    }

    const cart = await lastValueFrom(
      this.cartFacade.getActiveCart().pipe(take(1)),
    );

    this.ecommerceTrackingService.setBeginCheckoutEvent(
      cart.entries || [],
      cart.startDate || '',
      cart.endDate || '',
    );
  }

  async setPurchaseEvent() {
    if (this.activePageTemplate !== PageTemplate.ORDERCONFIRMATION) {
      return;
    }
    const orderDetails = await lastValueFrom(
      this.cartFacade.getOrderDetails().pipe(take(1)),
    );
    this.ecommerceTrackingService.setPurchaseEvent(orderDetails);
  }
}
